import { supabase } from "@/integrations/supabase/client";

type LogPayload = {
  message: string;
  stack?: string;
  severity?: "error" | "warn" | "info";
  source?: "frontend" | "boundary" | "vitals" | "network";
  context?: Record<string, unknown>;
};

const recent = new Map<string, number>();
const DEDUP_WINDOW_MS = 60_000;

export async function logError({
  message,
  stack,
  severity = "error",
  source = "frontend",
  context = {},
}: LogPayload) {
  if (!message) return;
  const key = `${severity}:${message.slice(0, 200)}`;
  const now = Date.now();
  const last = recent.get(key);
  if (last && now - last < DEDUP_WINDOW_MS) return;
  recent.set(key, now);
  if (recent.size > 100) {
    const oldest = [...recent.entries()].sort((a, b) => a[1] - b[1])[0]?.[0];
    if (oldest) recent.delete(oldest);
  }
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const payload = {
      user_id: user?.id ?? null,
      severity,
      source,
      page: typeof window !== "undefined" ? window.location.pathname : null,
      message: message.slice(0, 2000),
      stack: stack?.slice(0, 8000) ?? null,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
      context: context as never,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await supabase.from("client_errors").insert(payload as any);
  } catch {
    // Never throw from the logger.
  }
}

export function installGlobalErrorHandlers() {
  if (typeof window === "undefined") return;
  if ((window as unknown as { __errLoggerInstalled?: boolean }).__errLoggerInstalled) return;
  (window as unknown as { __errLoggerInstalled?: boolean }).__errLoggerInstalled = true;

  window.addEventListener("error", (e) => {
    logError({
      message: e.message || "window.onerror",
      stack: e.error?.stack,
      source: "frontend",
    });
  });

  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason;
    const message = typeof reason === "string"
      ? reason
      : reason?.message ?? "Unhandled promise rejection";
    logError({
      message,
      stack: reason?.stack,
      source: "frontend",
      context: { kind: "unhandledrejection" },
    });
  });
}